import React, { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Zap } from "lucide-react";
import {
  WorkflowRunStepOperation,
  AgentWorkflowRunStepOperationType,
  ToolCallSentOperationParameters,
} from "@/store/workflowRunStore";
import { useAgentWorkflowActionsStore } from "@/store/agentWorkflowActionsStore";
import { formatJson } from "@/modules/agents/components/helpers/jsonUtils";
import { formatRelative } from "date-fns";

interface CollapsedOperationDisplayProps {
  operation: WorkflowRunStepOperation;
}

/**
 * Displays a collapsible operation that is completed but not requiring special display.
 * Shows a green checkmark with the action name by default.
 * When expanded with a smooth animation, shows the input and output data inline underneath
 * with fixed height scrollable areas.
 */
export const CollapsedOperationDisplay: React.FC<
  CollapsedOperationDisplayProps
> = ({ operation }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getActionById } = useAgentWorkflowActionsStore();

  if (
    operation.operationParameters.type !==
    AgentWorkflowRunStepOperationType.TOOL_CALL
  ) {
    return null;
  }

  const actionId = operation.operationParameters.actionId;
  const action = actionId ? getActionById(actionId) : undefined;
  const actionName = action?.name || "Action";

  const params =
    operation.operationParameters as ToolCallSentOperationParameters;
  const formattedInput = formatJson(params.input);
  const formattedOutput = formatJson(params.output);

  return (
    <div>
      <div className="border rounded-lg overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs bg-muted hover:bg-muted/80 transition-colors group"
        >
          <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
          <Zap className="h-3 w-3 shrink-0" />
          <span className="flex-1 text-left font-medium">{actionName}</span>
          <span className="text-xs text-muted-foreground transition-opacity opacity-0 group-hover:opacity-100 shrink-0">
            Completed {formatRelative(operation.updatedAt, new Date())}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </button>
        <div
          className={`${isExpanded ? "max-h-[600px]" : "max-h-0"} transition-all duration-200 ease-in-out w-full min-w-0 overflow-hidden`}
        >
          <div className="p-3 space-y-3">
            {formattedInput && (
              <div className="space-y-2 w-full min-w-0">
                <h4 className="text-xs font-medium text-muted-foreground">
                  Input:
                </h4>
                <div className="text-xs bg-muted p-2 rounded overflow-y-auto max-h-48 w-full min-w-0 whitespace-pre-wrap break-words break-all font-mono">
                  {JSON.stringify(formattedInput, null, 2)}
                </div>
              </div>
            )}

            {params.error && (
              <div className="space-y-2 w-full min-w-0">
                <h4 className="text-xs font-medium text-red-500">Error:</h4>
                <div className="text-xs bg-red-50 text-red-900 p-2 rounded overflow-y-auto max-h-48 w-full min-w-0 whitespace-pre-wrap break-words break-all">
                  {params.error}
                </div>
              </div>
            )}

            {formattedOutput && (
              <div className="space-y-2 w-full min-w-0">
                <h4 className="text-xs font-medium text-muted-foreground">
                  Output:
                </h4>
                <div className="text-xs bg-muted p-2 rounded overflow-y-auto max-h-48 w-full min-w-0 whitespace-pre-wrap break-words break-all font-mono">
                  {JSON.stringify(formattedOutput, null, 2)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
